import { isWebotServerError } from "../../../../utils/contacts/contactWriteErrors";

/**
 * Hebrew copy, caps and arithmetic for the CRM bulk actions. PURE — no React, no hooks,
 * no requests, so every number and every sentence below is readable in one place.
 *
 * ── The caps here are COPY, not policy ────────────────────────────────────────────
 *
 * The server is the authority on every limit: it refuses over `maxBulkDelete` /
 * `maxBulkEdit` / `maxExportRows`, and it refuses rather than truncating. These constants
 * exist only so a dialog can say "עד 500 בבת אחת" BEFORE the round trip instead of after
 * it, and they are the server's DEFAULTS — an agency that raised or lowered its own cap
 * will see the server's refusal, which is surfaced verbatim.
 *
 * They are deliberately not read from `useCrmOrgSettings`: that hook answers 403 for a
 * caller in no agency, which is most of the fleet (measured 2026-08-16: 287 users are in
 * any org at all, across 96 orgs with a median of 2 members), so wiring the CRM list to it
 * would cost a request per page load and still leave the common case with nothing.
 */
export const CRM_BULK_LIMITS = {
  /** `DEFAULT_ORG_SETTINGS.maxBulkDelete`. */
  delete: 500,
  /** `DEFAULT_ORG_SETTINGS.maxBulkEdit`. Higher than delete because an edit is reversible. */
  edit: 2_000,
  /** `DEFAULT_ORG_SETTINGS.maxExportRows`. */
  export: 5_000,
  /** `MAX_GROUP_MEMBERS` in `whatsappGroups.service.ts` — and the campaign's own ceiling. */
  message: 5_000
} as const;

/**
 * The pacing a campaign actually sends at, and why a big audience is a multi-WEEK job.
 *
 * `computeSendBudget` (`whatsappSafetyRules.ts`) is
 * `floor(tierDailyUnique × qualityFactor)`, with `GREEN = 0.6` — the factor never reaches
 * 1.0 because hitting Meta's messaging limit puts the number into a 24h blackout, and the
 * headroom also absorbs the realtor's own manual sends. On a `TIER_1K` number that is
 * **600 unique recipients per day**; on the default tier assumed for an unknown number
 * (`TIER_250`) it is **150**.
 *
 * The browser does not compute the exact figure: the tier is mirrored on `whatsapp-number-access`
 * (since 2026-10-06), but the quality factor that scales it lives in the server's send rules, and
 * a second copy of that table here is a promise that drifts. Both numbers are therefore shown as a RANGE,
 * and a number above `TIER_1K` (1,200 a day at `TIER_2K`) finishes faster than its best case. Showing only the structural cap — "up to
 * 5,000 recipients" — is what produces campaigns that look stuck: a realtor picks 5,000,
 * sees 600 go out on day one, and reports a bug.
 *
 * Sends are additionally confined to 09:00–20:00 Asia/Jerusalem and blocked from Friday
 * 14:00 through Saturday, so the day count below is CALENDAR days at best.
 */
export const CAMPAIGN_DAILY_UNIQUE_BEST = 600;
export const CAMPAIGN_DAILY_UNIQUE_TYPICAL = 150;

export type CampaignDurationEstimate = {
  bestCaseDays: number;
  slowCaseDays: number;
  /** Hebrew, ready to render. */
  label: string;
};

export const estimateCampaignDuration = (recipients: number): CampaignDurationEstimate => {
  const bestCaseDays = Math.max(1, Math.ceil(recipients / CAMPAIGN_DAILY_UNIQUE_BEST));
  const slowCaseDays = Math.max(1, Math.ceil(recipients / CAMPAIGN_DAILY_UNIQUE_TYPICAL));
  if (bestCaseDays === 1 && slowCaseDays === 1) return { bestCaseDays, slowCaseDays, label: "יום אחד" };
  if (bestCaseDays === slowCaseDays) return { bestCaseDays, slowCaseDays, label: `כ-${bestCaseDays} ימים` };
  return { bestCaseDays, slowCaseDays, label: `בין ${bestCaseDays} ל-${slowCaseDays} ימים` };
};

/** `8102` → `8,102`. Every number in this UI is LTR digits inside RTL Hebrew. */
export const formatCount = (value: number): string => value.toLocaleString("en-US");

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/**
 * The drift refusal, told apart from every other 400.
 *
 * The server answers `CRM_INVALID_REQUEST` for a malformed selection, a busted cap AND
 * for drift, so the code alone cannot separate them — the drift message is matched on its
 * English prefix, which is stable in `assertSelectionCountMatches` and pinned by that
 * module's own test. ⚠️ A dedicated `CRM_SELECTION_DRIFTED` code would make this robust;
 * until it exists, an unmatched refusal falls through to the generic branch and shows the
 * server's own sentence rather than being swallowed.
 */
const DRIFT_PREFIX = "The selection changed";
/** `assertSelectionCountMatches`'s other branch: a CAPPED total cannot be verified at all. */
const CAPPED_PREFIX = "This filter matches at least";

export type CrmBulkFailure = {
  /** Hebrew, shown to the realtor. */
  message: string;
  /**
   * True when the list must be re-read before retrying. The UI reloads and asks the user
   * to look again; it must NEVER auto-retry with the server's number, which would be
   * acting on a set no human approved.
   */
  requiresReview: boolean;
};

/**
 * `"message"` was added when the bulk bar shipped, and it does NOT reach the CRM
 * endpoints at all — it creates a WhatsApp recipient group, whose refusals are about the
 * number and the list, never about contact permissions. It shares this function because
 * the drift/capped/scope branches above it are shaped by the SERVER envelope rather than
 * by the endpoint, and because a second copy of them is a second chance to swallow a
 * drift warning. Only the last-resort sentence differs.
 */
export const describeCrmBulkFailure = (
  error: unknown,
  action: "delete" | "edit" | "export" | "message"
): CrmBulkFailure => {
  if (isWebotServerError(error)) {
    const detail = error.serverMessage ?? "";

    // The agency's switch into (or out of) the pooled book is running: a 503 with the
    // plain-Hebrew retry sentence the server wrote for the screen (plan D4, decision 4).
    if (error.code === "CRM_BOOK_BUSY") {
      return { message: detail || "מאגר המשרד בתהליך איחוד, נסו שוב בעוד כמה דקות", requiresReview: false };
    }

    if (detail.startsWith(DRIFT_PREFIX)) {
      // Both numbers are in the server's sentence; re-stating them in Hebrew would mean
      // parsing it, and a parse that silently fails would print "undefined" on a
      // destructive confirmation. The instruction is what the realtor needs.
      return {
        message: "הרשימה השתנתה מאז שבחרת — ייתכן שנוספו או הוסרו לקוחות. רענַנו את הרשימה, בדקו את הבחירה ונסו שוב.",
        requiresReview: true
      };
    }

    if (detail.startsWith(CAPPED_PREFIX)) {
      return {
        message: "הסינון מחזיר יותר מדי לקוחות מכדי לאמת את הבחירה. צמצמו את הסינון ונסו שוב.",
        requiresReview: true
      };
    }

    if (error.status === 403 || error.code === "CRM_SCOPE_FORBIDDEN") {
      return {
        message:
          action === "delete"
            ? "אין לך הרשאה למחוק את הלקוחות האלה. פנו למנהל/ת המשרד."
            : action === "export"
            ? "אין לך הרשאה לייצא לקוחות. פנו למנהל/ת המשרד."
            : action === "message"
            ? "אין לך הרשאה לשלוח הודעות מהמספר הזה. פנו למנהל/ת המשרד."
            : "אין לך הרשאה לערוך לקוחות של סוכנים אחרים. פנו למנהל/ת המשרד.",
        requiresReview: false
      };
    }

    if (error.code === "CRM_SCOPE_AMBIGUOUS") {
      return { message: "יש לך יותר ממשרד אחד — בחרו משרד לפני הפעולה.", requiresReview: false };
    }

    // Every remaining 400 is a refusal with a REASON the server bothered to write (a cap,
    // an unknown field, an empty patch). Showing it beats a generic "failed": the realtor
    // can act on "at most 500 per bulk delete" and cannot act on "something went wrong".
    if (error.status === 400 && detail) return { message: detail, requiresReview: false };
  }

  const fallback =
    action === "delete"
      ? "מחיקת הלקוחות נכשלה."
      : action === "export"
      ? "הייצוא נכשל."
      : action === "message"
      ? "יצירת רשימת הנמענים נכשלה."
      : "העדכון נכשל.";
  return { message: `${fallback} נסו שוב בעוד רגע.`, requiresReview: false };
};

// ---------------------------------------------------------------------------
// Result summaries
// ---------------------------------------------------------------------------

/**
 * "12 נמחקו · 3 כבר היו מחוקים".
 *
 * The skipped rows are REPORTED rather than folded into the headline number, because they
 * are facts about the data and not failures: a contact that was already deleted, or one
 * whose values already matched the patch, is a success with nothing to do. Hiding them
 * makes "8,102 selected → 8,099 changed" look like three silent failures.
 */
export const summarizeSkips = (
  skipped: readonly { reason: string }[],
  labels: Record<string, string>
): string => {
  const counts = new Map<string, number>();
  skipped.forEach((entry) => counts.set(entry.reason, (counts.get(entry.reason) ?? 0) + 1));
  return [...counts.entries()]
    .map(([reason, count]) => `${formatCount(count)} ${labels[reason] ?? reason}`)
    .join(" · ");
};

export const DELETE_SKIP_LABELS: Record<string, string> = {
  not_found: "לא נמצאו",
  // The server stopped emitting this when the delete became a real delete — "already
  // deleted" and "not found" are now the same state. Kept because a file this old outlives
  // one deploy: an in-flight response from the previous version must still render a label
  // rather than a blank.
  already_deleted: "כבר היו מחוקים",
  // The pooled book's "remove from my list" on people who were not on it to begin with.
  // It always follows the receipt (`describeDeleteOutcome`), which names the list.
  not_held: "לא היו אצלך",
  error: "נכשלו"
};

/**
 * The bulk delete's receipt, in the unit of what the server DID: the documents are gone,
 * or the caller came off the people's lists and the office's records stand (the pooled
 * book's default — `CrmContactsDeleteResponse.mode`). An older server's answer carries no
 * `mode` and can only have deleted records.
 */
export const describeDeleteOutcome = (deleted: number, mode: "holder" | "record" | undefined): string =>
  mode === "holder" ? `${formatCount(deleted)} לקוחות הוסרו מ"הלקוחות שלי"` : `${formatCount(deleted)} לקוחות נמחקו`;

export const EDIT_SKIP_LABELS: Record<string, string> = {
  not_found: "לא נמצאו",
  deleted: "מחוקים",
  unchanged: "כבר היו מעודכנים",
  error: "נכשלו"
};
